import { HubConnection, HubConnectionBuilder, type IHttpConnectionOptions } from '@microsoft/signalr';
import { onBeforeUnmount, type Ref, ref } from 'vue';
import { ActionQueue } from './utils/action-queue';
import type { SignalRConfig } from './signalRconfig';

/**
 * A service to integrate SignalR with Vue.js
 */
export class SignalRService {
    /** The current SignalR connection object */
    public connection: HubConnection | null = null;
    private readonly connectionBuilder: HubConnectionBuilder;

    private config: SignalRConfig;
    private initiated = false;
    private connected = ref(false);

    private invokeQueue = new ActionQueue();
    private successQueue = new ActionQueue();

    public constructor(config: SignalRConfig, connectionBuilder: HubConnectionBuilder) {
        this.connectionBuilder = connectionBuilder;
        this.config = {
            automaticUnsubscribe: true,
            ...config
        };

        if (config.url !== '') {
            this.init();
        }
    }

    public init(): void {
        if (this.connection) {
            console.warn('SignalRService connection is already initialized.');
        } else {
            const connection = this.buildConnection(this.connectionBuilder);
            this.configureConnection(connection);
            this.connection = connection;
        }
    }

    /** Start the connection; called automatically when the plugin is registered */
    public async start(): Promise<void> {
        if (!this.connection) {
            throw new Error('SignalRService connection not yet initialized. Make sure you call init() before start.');
        }

        await this.connection.start();

        this.initiated = true;
        this.connected.value = true;

        this.invokeQueue.resolve();
        this.successQueue.resolve();
    }

    /** Stop the connection; unlike a dropped connection, a stopped one does not reconnect automatically */
    public async stop(): Promise<void> {
        await this.connection?.stop();
    }

    /** Set a callback to trigger when a connection to the hub is successfully established */
    public connectionSuccess(callback: () => void): void {
        if (this.initiated) {
            callback();
        } else {
            this.successQueue.enqueue(callback);
        }
    }

    /**
     * Send a command to the SignalR hub
     * @param target The name or token of the command to send to
     * @param message The payload to send to the command
     * @returns a promise the resolves with the event returns a value
     */
    public invoke<TResponse, TMessage = null>(target: string, message?: TMessage): Promise<TResponse> {
        const invoke = (): Promise<TResponse> => {
            if (!this.connection) {
                return Promise.reject(new Error('SignalRService connection not yet initialized.'));
            }

            return typeof message !== 'undefined'
                ? this.connection.invoke(target, message)
                : this.connection.invoke(target);
        };

        if (this.initiated) {
            return invoke();
        }

        return new Promise<TResponse>((res, rej) => this.invokeQueue.enqueue(() => invoke().then(res, rej)));
    }

    /**
     * Send a command to the SignalR hub without awaiting a response
     * @param target The name or token of the command to send to
     * @param message The payload to send to the command
     */
    public send<T>(target: string, message?: T): Promise<void> {
        const send = (): Promise<void> => {
            if (!this.connection) {
                return Promise.reject(new Error('SignalRService connection not yet initialized.'));
            }

            return typeof message !== 'undefined'
                ? this.connection.send(target, message)
                : this.connection.send(target);
        };

        if (this.initiated) {
            return send();
        }

        return new Promise<void>((res, rej) => this.invokeQueue.enqueue(() => send().then(res, rej)));
    }

    /**
     * Subscribe to an event on the hub
     * @param target The name or token of the event to listen to
     * @param callback The callback to trigger with the hub sends the event
     * @param autoUnsubscribe Override options.automaticUnsubscribe config
     */
    public on<T>(target: string, callback: (arg: T) => void, autoUnsubscribe = this.config.automaticUnsubscribe): void {
        this.connection?.on(target, callback);
        if (autoUnsubscribe) {
            onBeforeUnmount(() => this.off(target, callback));
        }
    }

    /**
     * Unsubscribe from an event on the hub
     * @param target The name or token of the event to unsubscribe from
     * @param callback The specific callback to unsubscribe. If none is provided, all listeners on the target will be unsubscribed
     */
    public off<T>(target: string, callback?: (arg: T) => void): void {
        if (callback) {
            this.connection?.off(target, callback);
        } else {
            this.connection?.off(target);
        }
    }

    /** Get a reactive connection status */
    public getConnectionStatus(): Ref<boolean> {
        return this.connected;
    }

    public set options(value: SignalRConfig) {
        if (this.connection) {
            throw new Error('Cannot change SignalRService options after initialization.');
        }

        this.config = {
            automaticUnsubscribe: true,
            ...value
        };
    }

    private buildConnection(builder: HubConnectionBuilder): HubConnection {
        const options = this.config;
        const connOptions: IHttpConnectionOptions = {};

        if (options.accessTokenFactory) {
            connOptions.accessTokenFactory = options.accessTokenFactory;
        }

        if (options.prebuild) {
            options.prebuild(builder, connOptions);
        }

        builder.withUrl(options.url, connOptions);

        if (options.automaticReconnect) {
            builder.withAutomaticReconnect();
        }

        return builder.build();
    }

    private configureConnection(connection: HubConnection): void {
        connection.onreconnected(() => this.reconnect());

        connection.onreconnecting(err => {
            this.fail(err);
            this.reconnecting();
        });

        connection.onclose(err => {
            this.fail(err);
            this.config.closed?.call(null, err);
        });
    }

    private reconnect(): void {
        this.connected.value = true;
        this.config.reconnected?.call(null);
    }

    private reconnecting(): void {
        this.config.reconnecting?.call(null);
    }

    private fail(reason?: unknown): void {
        this.connected.value = false;
        this.config.disconnected?.call(this, reason);
    }
}
